import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import { useSiteScope } from '../../core/site-scope';
import { submitQueueable } from '../../offline-queue';
import type { OpenPoDto, PoolLabelDto, ReceiveBody, ReceivingSlipDetailDto } from './receiving.types';

const BASE = '/v1/itsm/receiving';
const KEY = ['receiving'] as const;

/** Open POs with outstanding lines, each carrying its label-pool count so the
 * screen can warn about a short pool BEFORE anybody starts scanning.
 * ⚠ `receiving: read` since R-234 P0 (it was `warehouse` before). */
export function useOpenPos() {
  const { scope, ready } = useSiteScope();
  return useQuery({
    enabled: ready,
    queryKey: [...KEY, scope, 'open-pos'],
    queryFn: () => api.get<OpenPoDto[]>(`${BASE}/open-pos`),
    refetchInterval: 30_000,
  });
}

/** The UNUSED labels of one class — the tags a receipt may actually bind.
 *
 * `open-pos` carries only a COUNT per line, which is enough to warn about a short
 * pool but not enough to let the storekeeper say WHICH sticker went on which box.
 * The receive modal needs the tags themselves.
 *
 * Keyed under `asset-labels` so the receive mutation's existing invalidation of
 * that key drops it too — a bound label must never still look unused. */
export function useUnusedLabels(classCode: string | null | undefined) {
  const { scope, ready } = useSiteScope();
  return useQuery({
    enabled: ready && !!classCode,
    queryKey: ['asset-labels', scope, 'unused', classCode],
    queryFn: () =>
      api.get<PoolLabelDto[]>(`/v1/assets/labels?state=unused&class_code=${encodeURIComponent(classCode!)}`),
  });
}

/** Goods receipts already booked — the store's own history. */
export function useReceivingSlips() {
  const { scope, ready } = useSiteScope();
  return useQuery({
    enabled: ready,
    queryKey: [...KEY, scope, 'slips'],
    queryFn: () => api.get<ReceivingSlipDetailDto[]>(`${BASE}/slips`),
  });
}

export function useReceivingSlip(id: string | null | undefined) {
  const { scope, ready } = useSiteScope();
  return useQuery({
    queryKey: [...KEY, scope, 'slips', id],
    queryFn: () => api.get<ReceivingSlipDetailDto>(`${BASE}/slips/${id}`),
    enabled: ready && !!id,
  });
}

/** Receive goods against a PO. Offline-queued and exactly-once via `client_ref`
 * — an offline replay after reconnect returns the existing slip server-side and
 * is dropped as already-applied.
 *
 * ⚠ THIS IS WHAT CREATES ASSETS. One asset per unit, each bound to a pool label
 * and written into the chosen stockroom, plus a movement row. Nothing earlier in
 * the chain mints a record. */
export function useReceive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReceiveBody) =>
      submitQueueable<ReceivingSlipDetailDto>({
        method: 'POST',
        path: BASE,
        body,
        invalidate: [...KEY],
        label: `Receive ${body.po_number}`,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      // Receiving mints asset records and consumes labels — refresh the
      // register, the stockroom and the label pool too.
      void qc.invalidateQueries({ queryKey: ['site-assets'] });
      void qc.invalidateQueries({ queryKey: ['asset-labels'] });
      // The PO's counters moved, which moves the request's state with them.
      void qc.invalidateQueries({ queryKey: ['procurement'] });
    },
  });
}
